import React, { useEffect, useMemo, useState } from "react";
import { reportsApi, productApi, customersApi, expensesApi } from "../api";
import { useI18n } from "../i18n";
import { useAccount } from "../contexts/AccountContext";
import { getTimeframe, getCurrentFYRange, toIsoRange } from "../timeframe";

const currencyFormatterZero = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const currencyFormatterTwo = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

function formatCurrency(value, fraction = 0) {
  const num = Number.isFinite(value) ? value : 0;
  return fraction === 2 ? currencyFormatterTwo.format(num) : currencyFormatterZero.format(num);
}

function formatPercent(value) {
  if (!Number.isFinite(value)) return "—";
  return `${(value * 100).toFixed(1)}%`;
}

function formatDateInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function normalizeRange(range) {
  if (!range?.from || !range?.to) return { from: range?.from || "", to: range?.to || "" };
  if (range.from <= range.to) return range;
  return { from: range.to, to: range.from };
}

function buildOptionList(raw, valueKey, labelKey) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => ({
      value: String(item?.[valueKey] || "").trim(),
      label: String(item?.[labelKey] || "").trim() || "—",
    }))
    .filter((item) => !!item.value);
}

function buildCategoryOptions(data) {
  if (!data) return [];
  if (Array.isArray(data)) {
    return data
      .map((name) => ({ value: String(name || "").trim(), label: String(name || "").trim() }))
      .filter((item) => !!item.value);
  }
  const defaults = Array.isArray(data.defaults) ? data.defaults : [];
  const customs = Array.isArray(data.customs) ? data.customs : [];
  const unique = Array.from(new Set([...defaults, ...customs]));
  return unique
    .map((name) => ({ value: String(name || "").trim(), label: String(name || "").trim() }))
    .filter((item) => !!item.value);
}

function ChipMultiSelect({
  label,
  options,
  selected,
  onChange,
  placeholder,
  loading,
}) {
  const toggle = (value) => {
    if (!value) return;
    onChange(
      selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value]
    );
  };

  return (
    <div className="bg-white border border-green-100 rounded-xl p-3 shadow-sm">
      <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">{label}</div>
      {loading ? (
        <div className="text-xs text-gray-500">…</div>
      ) : options.length === 0 ? (
        <div className="text-xs text-gray-400">{placeholder}</div>
      ) : (
        <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto pr-1">
          {options.map((option) => {
            const active = selected.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                  active
                    ? "bg-green-600 text-white border-green-600 shadow"
                    : "bg-white text-gray-700 border-green-200"
                }`}
                onClick={() => toggle(option.value)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}
      {selected.length > 0 && (
        <button
          type="button"
          className="mt-2 text-[11px] text-green-600 underline"
          onClick={() => onChange([])}
        >
          Clear
        </button>
      )}
    </div>
  );
}

function DrilldownCard({ title, name, value, subtitle, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-2xl border p-4 shadow-sm transition ${
        active ? "border-green-500 bg-green-50" : "border-green-100 bg-white hover:border-green-400"
      }`}
    >
      <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">{title}</div>
      <div className="text-lg font-semibold text-gray-900 truncate">{name || "—"}</div>
      <div className="text-sm text-green-700 font-semibold">{value}</div>
      <div className="text-[11px] text-gray-500 mt-1">{subtitle}</div>
    </button>
  );
}

function NetTrendChart({ title, data }) {
  if (!Array.isArray(data) || data.length === 0) return null;
  const values = data.map((item) => Number(item.net || 0));
  const max = Math.max(0, ...values);
  const min = Math.min(0, ...values);
  const range = max - min || 1;
  const points = data
    .map((item, index) => {
      const x = data.length === 1 ? 0 : (index / (data.length - 1)) * 100;
      const y = 100 - ((Number(item.net || 0) - min) / range) * 100;
      return `${x},${y}`;
    })
    .join(" ");
  const areaPoints = `0,100 ${points} 100,100`;
  const baseline = 100 - ((0 - min) / range) * 100;

  return (
    <div className="bg-white border border-green-100 rounded-2xl p-4 shadow-sm">
      <div className="text-sm font-semibold text-gray-800 mb-2">{title}</div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-32">
        <polyline
          fill="rgba(16, 185, 129, 0.15)"
          stroke="none"
          points={areaPoints}
        />
        <polyline
          fill="none"
          stroke="#16a34a"
          strokeWidth="2"
          points={points}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <line
          x1="0"
          x2="100"
          y1={baseline}
          y2={baseline}
          stroke="#e5e7eb"
          strokeDasharray="2 4"
        />
      </svg>
      <div className="flex justify-between text-[10px] text-gray-500 mt-2">
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="flex items-center gap-3 text-sm text-gray-600">
        <div className="h-6 w-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
        <span>Crunching numbers…</span>
      </div>
    </div>
  );
}

export default function Reports() {
  const { t } = useI18n();
  const { selectedAccountId, viewMode } = useAccount();
  const initialRange = useMemo(() => {
    const tf = getTimeframe();
    return { from: tf.from, to: tf.to };
  }, []);

  const [online, setOnline] = useState(() => {
    if (typeof navigator === "undefined") return true;
    return navigator.onLine;
  });

  const [pendingRange, setPendingRange] = useState(initialRange);
  const [pendingQuick, setPendingQuick] = useState("global");
  const [pendingOrderFilters, setPendingOrderFilters] = useState({
    products: [],
    customers: [],
    categories: [],
  });
  const [pendingExpenseFilters, setPendingExpenseFilters] = useState({
    categories: [],
  });

  const [appliedRange, setAppliedRange] = useState(normalizeRange(initialRange));
  const [orderFilters, setOrderFilters] = useState({
    products: [],
    customers: [],
    categories: [],
  });
  const [expenseFilters, setExpenseFilters] = useState({
    categories: [],
  });

  const [options, setOptions] = useState({
    products: [],
    customers: [],
    orderCategories: [],
    expenseCategories: [],
  });
  const [optionsLoading, setOptionsLoading] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeDrilldown, setActiveDrilldown] = useState("products");

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(() => {
    if (!online) return;
    let cancelled = false;
    setOptionsLoading(true);
    Promise.all([
      productApi.list().catch(() => []),
      customersApi.list().catch(() => []),
      expensesApi.listUnified("income").catch(() => ({ defaults: [], customs: [] })),
      expensesApi.listUnified("expense").catch(() => ({ defaults: [], customs: [] })),
    ])
      .then(([products, customers, incomeCats, expenseCats]) => {
        if (cancelled) return;
        setOptions({
          products: buildOptionList(products, "productId", "name"),
          customers: buildOptionList(customers, "customerId", "name"),
          orderCategories: buildCategoryOptions(incomeCats),
          expenseCategories: buildCategoryOptions(expenseCats),
        });
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [online]);

  useEffect(() => {
    if (!online) return;
    const { from, to } = appliedRange;
    if (!from || !to) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const iso = toIsoRange({ from, to });
    const payload = {
      from: iso.from,
      to: iso.to,
      orders: {
        productIds: orderFilters.products,
        customerIds: orderFilters.customers,
        categories: orderFilters.categories,
      },
      expenses: {
        categories: expenseFilters.categories,
      },
    };
    if (viewMode === "account" && selectedAccountId) {
      payload.accountId = selectedAccountId;
    }

    reportsApi
      .summary(payload)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Failed to load report");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [online, appliedRange.from, appliedRange.to, orderFilters, expenseFilters, viewMode, selectedAccountId]);

  const productNameMap = useMemo(() => {
    const map = new Map();
    options.products.forEach((item) => map.set(item.value, item.label));
    return map;
  }, [options.products]);

  const customerNameMap = useMemo(() => {
    const map = new Map();
    options.customers.forEach((item) => map.set(item.value, item.label));
    return map;
  }, [options.customers]);

  const productList = data?.orders?.allProducts || data?.orders?.topProducts || [];
  const customerList = data?.orders?.allCustomers || data?.orders?.topCustomers || [];
  const expenseList = data?.expenses?.allCategories || data?.expenses?.topCategories || [];
  const pendingList =
    data?.cashCollections?.allPendingCustomers || data?.cashCollections?.topPendingCustomers || [];
  const cashSummary = data?.cashCollections;

  const drilldownCards = useMemo(() => {
    const pendingTotalAmount = cashSummary?.totalCashPending || 0;
    const pendingOrders = cashSummary?.pendingOrders || 0;
    const trackedOrders = cashSummary?.trackedOrders || 0;
    const collectionRate = cashSummary?.collectionRate ?? null;
    const pendingValue =
      trackedOrders > 0
        ? t("reports.drilldown.pendingBills", "{pending}/{total} bills pending")
            .replace("{pending}", String(pendingOrders))
            .replace("{total}", String(trackedOrders))
        : t("reports.drilldown.pendingBillsFallback", "{pending} bills pending").replace(
            "{pending}",
            String(pendingOrders)
          );
    const pendingSubtitle =
      collectionRate !== null && trackedOrders > 0
        ? t("reports.drilldown.collectionRateLabel", "Collection rate {value}").replace(
            "{value}",
            formatPercent(collectionRate || 0)
          )
        : t("reports.drilldown.pendingNoData", "No collection data yet");

    return [
      {
        key: "products",
        title: t("reports.drilldown.products", "Top product"),
        listTitle: t("reports.drilldown.productsAll", "All products"),
        name: productList[0]?.name || "—",
        value: productList[0] ? formatCurrency(productList[0].revenue || 0, 2) : "—",
        subtitle: productList[0]
          ? t("reports.drilldown.shareOfRevenue", "{value} of revenue").replace(
              "{value}",
              formatPercent(productList[0].share || 0)
            )
          : t("reports.drilldown.none", "No data yet"),
        list: productList,
      },
      {
        key: "customers",
        title: t("reports.drilldown.customers", "Top customer"),
        listTitle: t("reports.drilldown.customersAll", "All customers"),
        name:
          customerList[0]?.name ||
          customerNameMap.get(customerList[0]?.id || "") ||
          "—",
        value: customerList[0] ? formatCurrency(customerList[0].revenue || 0, 2) : "—",
        subtitle: customerList[0]
          ? t("reports.drilldown.shareOfRevenue", "{value} of revenue").replace(
              "{value}",
              formatPercent(customerList[0].share || 0)
            )
          : t("reports.drilldown.none", "No data yet"),
        list: customerList,
      },
      {
        key: "expenses",
        title: t("reports.drilldown.expenses", "Top expense category"),
        listTitle: t("reports.drilldown.expensesAll", "All expense categories"),
        name: expenseList[0]?.name || "—",
        value: expenseList[0] ? formatCurrency(expenseList[0].amount || 0, 2) : "—",
        subtitle: expenseList[0]
          ? t("reports.drilldown.shareOfExpense", "{value} of spend").replace(
              "{value}",
              formatPercent(expenseList[0].share || 0)
            )
          : t("reports.drilldown.none", "No data yet"),
        list: expenseList,
      },
      {
        key: "pending",
        title: t("reports.drilldown.pending", "Cash pending"),
        listTitle: t("reports.drilldown.pendingAll", "All pending customers"),
        name: formatCurrency(pendingTotalAmount, 2),
        value: pendingValue,
        subtitle: pendingSubtitle,
        list: pendingList,
      },
    ];
  }, [cashSummary, customerList, expenseList, pendingList, productList, customerNameMap, formatCurrency, formatPercent, t]);

  useEffect(() => {
    if (drilldownCards.length === 0) return;
    const current = drilldownCards.find(
      (card) => card.key === activeDrilldown && card.list.length > 0
    );
    if (!current) {
      const fallback = drilldownCards.find((card) => card.list.length > 0);
      if (fallback) {
        setActiveDrilldown(fallback.key);
      }
    }
  }, [activeDrilldown, drilldownCards]);

  const activeCard =
    drilldownCards.find((card) => card.key === activeDrilldown) || drilldownCards[0];

  const hasFilters =
    orderFilters.products.length > 0 ||
    orderFilters.customers.length > 0 ||
    orderFilters.categories.length > 0 ||
    expenseFilters.categories.length > 0;

  const summary = data?.summary || {};

  const handleQuickRange = (key) => {
    setPendingQuick(key);
    const today = new Date();
    let from = pendingRange.from;
    let to = pendingRange.to;
    if (key === "global") {
      const tf = getTimeframe();
      from = tf.from;
      to = tf.to;
    } else if (key === "7d") {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 6);
      from = formatDateInput(start);
      to = formatDateInput(end);
    } else if (key === "30d") {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 29);
      from = formatDateInput(start);
      to = formatDateInput(end);
    } else if (key === "fy") {
      const range = getCurrentFYRange();
      from = range.from;
      to = range.to;
    } else {
      // custom
      setPendingQuick("custom");
      return;
    }
    setPendingRange({ from, to });
  };

  const handleApply = () => {
    if (!pendingRange.from || !pendingRange.to) {
      setError("Please select a valid date range");
      return;
    }
    const normalized = normalizeRange(pendingRange);
    setAppliedRange(normalized);
    setOrderFilters({
      products: [...pendingOrderFilters.products],
      customers: [...pendingOrderFilters.customers],
      categories: [...pendingOrderFilters.categories],
    });
    setExpenseFilters({
      categories: [...pendingExpenseFilters.categories],
    });
    setError(null);
  };

  const handleReset = () => {
    const tf = getTimeframe();
    const defaultRange = { from: tf.from, to: tf.to };
    setPendingRange(defaultRange);
    setPendingQuick("global");
    setPendingOrderFilters({ products: [], customers: [], categories: [] });
    setPendingExpenseFilters({ categories: [] });
    setAppliedRange(normalizeRange(defaultRange));
    setOrderFilters({ products: [], customers: [], categories: [] });
    setExpenseFilters({ categories: [] });
    setError(null);
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 p-4 pb-20">
      <div className="mb-5">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
          {t("reports.title", "Reports")}
        </h1>
        <p className="text-sm text-gray-600">
          {t("reports.subtitle", "Analyse your business performance in depth.")}
        </p>
      </div>

      {!online && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 mb-4">
          <div className="text-sm font-semibold text-yellow-800">
            {t("reports.offline.title", "Reporting requires internet")}
          </div>
          <div className="text-xs text-yellow-700 mt-1">
            {t(
              "reports.offline.desc",
              "Reconnect to refresh insights. Offline data entry is still available."
            )}
          </div>
        </div>
      )}

      <div className="bg-white border border-green-100 rounded-2xl p-4 mb-4 shadow-sm space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">
            {t("reports.range.quick", "Quick ranges")}
          </div>
          <div className="flex flex-wrap gap-2">
            {["global", "7d", "30d", "fy", "custom"].map((key) => {
              const labels = {
                global: t("reports.range.global", "Use dashboard period"),
                "7d": t("reports.range.7d", "Last 7 days"),
                "30d": t("reports.range.30d", "Last 30 days"),
                fy: t("reports.range.fy", "Current FY"),
                custom: t("reports.range.custom", "Custom"),
              };
              const active = pendingQuick === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    active
                      ? "bg-green-600 text-white border-green-600"
                      : "bg-white text-green-700 border-green-200"
                  }`}
                  onClick={() => handleQuickRange(key)}
                >
                  {labels[key]}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="col-span-1">
            <div className="text-[11px] text-gray-500 mb-1">
              {t("reports.field.from", "From")}
            </div>
            <input
              type="date"
              value={pendingRange.from}
              onChange={(e) => {
                setPendingQuick("custom");
                setPendingRange((prev) => ({ ...prev, from: e.target.value }));
              }}
              className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="col-span-1">
            <div className="text-[11px] text-gray-500 mb-1">
              {t("reports.field.to", "To")}
            </div>
            <input
              type="date"
              value={pendingRange.to}
              onChange={(e) => {
                setPendingQuick("custom");
                setPendingRange((prev) => ({ ...prev, to: e.target.value }));
              }}
              className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="col-span-2 flex items-end justify-end gap-2">
            <button
              type="button"
              className="px-3 py-2 text-sm rounded-lg border border-green-200 text-green-700"
              onClick={handleReset}
            >
              {t("reports.actions.reset", "Reset")}
            </button>
            <button
              type="button"
              className="px-4 py-2 text-sm rounded-lg bg-green-600 text-white shadow"
              onClick={handleApply}
              disabled={!online}
            >
              {t("reports.actions.run", "Run report")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <ChipMultiSelect
            label={t("reports.filters.orders.products", "Products")}
            options={options.products}
            selected={pendingOrderFilters.products}
            placeholder={t("reports.filters.products.placeholder", "No products yet")}
            onChange={(next) => setPendingOrderFilters((prev) => ({ ...prev, products: next }))}
            loading={optionsLoading}
          />
          <ChipMultiSelect
            label={t("reports.filters.orders.customers", "Customers")}
            options={options.customers}
            selected={pendingOrderFilters.customers}
            placeholder={t("reports.filters.customers.placeholder", "No customers yet")}
            onChange={(next) => setPendingOrderFilters((prev) => ({ ...prev, customers: next }))}
            loading={optionsLoading}
          />
          <ChipMultiSelect
            label={t("reports.filters.orders.categories", "Income categories")}
            options={options.orderCategories}
            selected={pendingOrderFilters.categories}
            placeholder={t("reports.filters.categories.placeholder", "No categories yet")}
            onChange={(next) => setPendingOrderFilters((prev) => ({ ...prev, categories: next }))}
            loading={optionsLoading}
          />
          <ChipMultiSelect
            label={t("reports.filters.expenses.categories", "Expense categories")}
            options={options.expenseCategories}
            selected={pendingExpenseFilters.categories}
            placeholder={t("reports.filters.expenseCategories.placeholder", "No expense categories yet")}
            onChange={(next) => setPendingExpenseFilters({ categories: next })}
            loading={optionsLoading}
          />
        </div>
      </div>

      <div className="text-xs text-gray-500 mb-3">
        {viewMode === "account" && selectedAccountId
          ? t("reports.filters.accountScope.account", "Showing data for selected account")
          : t("reports.filters.accountScope.org", "Showing organization-wide data")}
      </div>

      {hasFilters ? (
        <div className="text-xs text-green-700 mb-4">
          {t("reports.filters.applied", "Filters active")} • {appliedRange.from} → {appliedRange.to}
        </div>
      ) : (
        <div className="text-xs text-gray-500 mb-4">
          {t("reports.filters.none", "No filters applied")} • {appliedRange.from} → {appliedRange.to}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-4 text-sm text-red-700">
          {t("reports.error.generic", "Failed to load report. Please try again.")}
        </div>
      )}

      {loading && <LoadingState />}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            <SummaryCard
              label={t("reports.summary.revenue", "Revenue")}
              value={formatCurrency(summary.totalRevenue || 0)}
            />
            <SummaryCard
              label={t("reports.summary.cost", "COGS")}
              value={formatCurrency(summary.totalCost || 0)}
            />
            <SummaryCard
              label={t("reports.summary.expenses", "Expenses")}
              value={formatCurrency(summary.totalExpenses || 0)}
            />
            <SummaryCard
              label={t("reports.summary.gross", "Gross Profit")}
              value={formatCurrency(summary.grossProfit || 0)}
            />
            <SummaryCard
              label={t("reports.summary.net", "Net Profit")}
              value={formatCurrency(summary.netProfit || 0)}
              tone={(summary.netProfit || 0) >= 0 ? "positive" : "negative"}
            />
            <SummaryCard
              label={t("reports.summary.margin", "Profit Margin")}
              value={formatPercent(summary.profitMargin || 0)}
            />
          </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {drilldownCards.map((card) => (
            <DrilldownCard
              key={card.key}
              title={card.title}
              name={card.name}
              value={card.value}
              subtitle={card.subtitle}
              active={activeCard?.key === card.key}
              onClick={() => setActiveDrilldown(card.key)}
            />
          ))}
        </div>

        {activeCard && (
          <DrilldownList
            type={activeCard.key}
            title={activeCard.listTitle}
            items={activeCard.list}
            formatCurrency={formatCurrency}
            formatPercent={formatPercent}
            t={t}
          />
        )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <NetTrendChart
              title={t("reports.trend.dailyNet", "Daily net profit")}
              data={data?.combined?.trend || []}
            />
            <RecentTable
              title={t("reports.trend.recent", "Recent days")}
              data={data?.combined?.trend?.slice(-7) || []}
            />
          </div>

          <div className="text-[11px] text-gray-500">
            {t("reports.generatedAt", "Updated at {time}").replace(
              "{time}",
              data?.generatedAt ? new Date(data.generatedAt).toLocaleString() : "—"
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, tone = "neutral" }) {
  const toneClasses = {
    neutral: "bg-green-50 border-green-100 text-green-700",
    positive: "bg-green-100 border-green-200 text-green-800",
    negative: "bg-red-50 border-red-100 text-red-700",
  };
  const toneKey = toneClasses[tone] ? tone : "neutral";

  return (
    <div className={`rounded-xl p-3 border text-center shadow-sm ${toneClasses[toneKey]}`}>
      <div className="text-[11px] uppercase tracking-wide text-gray-600 mb-1">{label}</div>
      <div className="text-sm sm:text-base font-semibold">{value}</div>
    </div>
  );
}

function DrilldownList({ type, title, items, formatCurrency, formatPercent, t }) {
  if (!items || items.length === 0) {
    return (
      <div className="bg-white border border-green-100 rounded-2xl p-4 shadow-sm mb-4">
        <div className="text-sm font-semibold text-gray-800 mb-1">{title}</div>
        <div className="text-xs text-gray-500">
          {t("reports.drilldown.none", "No data yet")}
        </div>
      </div>
    );
  }

  let columns;
  let rows;

  switch (type) {
    case "customers":
      columns = [
        { key: "name", label: t("reports.drilldown.name", "Name") },
        { key: "value", label: t("reports.drilldown.sales", "Sales") },
        { key: "orders", label: t("reports.drilldown.orders", "Orders") },
        { key: "share", label: t("reports.drilldown.share", "Share") },
      ];
      rows = items.map((item) => ({
        key: item.id || item.name,
        name: item.name || "—",
        value: formatCurrency(item.revenue || 0, 2),
        orders: item.orders || 0,
        share: formatPercent(item.share || 0),
      }));
      break;
    case "expenses":
      columns = [
        { key: "name", label: t("reports.drilldown.name", "Name") },
        { key: "value", label: t("reports.drilldown.amount", "Amount") },
        { key: "count", label: t("reports.drilldown.entries", "Entries") },
        { key: "share", label: t("reports.drilldown.share", "Share") },
      ];
      rows = items.map((item) => ({
        key: item.id || item.name,
        name: item.name || "—",
        value: formatCurrency(item.amount || 0, 2),
        count: item.count || 0,
        share: formatPercent(item.share || 0),
      }));
      break;
    case "pending":
      columns = [
        { key: "name", label: t("reports.drilldown.name", "Name") },
        { key: "value", label: t("reports.drilldown.pendingAmount", "Pending") },
        { key: "orders", label: t("reports.drilldown.orders", "Bills") },
        { key: "share", label: t("reports.drilldown.share", "Share") },
      ];
      rows = items.map((item) => ({
        key: item.id || item.name,
        name: item.name || "—",
        value: formatCurrency(item.pending || 0, 2),
        orders: item.orders || 0,
        share: formatPercent(item.share || 0),
      }));
      break;
    default:
      columns = [
        { key: "name", label: t("reports.drilldown.name", "Name") },
        { key: "value", label: t("reports.drilldown.sales", "Sales") },
        { key: "orders", label: t("reports.drilldown.orders", "Orders") },
        { key: "share", label: t("reports.drilldown.share", "Share") },
      ];
      rows = items.map((item) => ({
        key: item.id || item.name,
        name: item.name || "—",
        value: formatCurrency(item.revenue || 0, 2),
        orders: item.orders || 0,
        share: formatPercent(item.share || 0),
      }));
      break;
  }

  return (
    <div className="bg-white border border-green-100 rounded-2xl p-4 shadow-sm mb-6">
      <div className="text-sm font-semibold text-gray-800 mb-3">{title}</div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-gray-500">
              {columns.map((col) => (
                <th key={col.key} className="pb-2 pr-4 font-medium">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.key || index} className="border-t border-green-100 text-gray-800">
                {columns.map((col) => (
                  <td key={col.key} className="py-2 pr-4">
                    {row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RecentTable({ title, data }) {
  if (!Array.isArray(data) || data.length === 0) return null;
  return (
    <div className="bg-white border border-green-100 rounded-2xl p-4 shadow-sm">
      <div className="text-sm font-semibold text-gray-800 mb-3">{title}</div>
      <div className="space-y-2">
        {data
          .map((row) => ({ ...row, date: row.date }))
          .reverse()
          .map((row) => (
            <div key={row.date} className="flex items-center justify-between text-sm">
              <div className="text-gray-600">{row.date}</div>
              <div className={`font-semibold ${row.net >= 0 ? "text-green-600" : "text-red-600"}`}>
                {formatCurrency(row.net || 0)}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}


